import { faMinus, faPlus, faShieldHalved } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HighlightedText } from 'components/text/HighlightedText';
import {
    ChildrenLozenge,
    FaultTypeTreeItem,
    FaultTypeTreeItemWithTrailer,
} from 'pages/Desk/issues/fault_type/styled';
import { IssueFaultType } from 'pages/Desk/issues/fault_type/useFaultTypes';
import { TreeNode } from 'shared/data_tree/types';
import { stopEvent } from 'utils/event';

type FaultTypeChildrenSelectorProps = Readonly<{
    node: TreeNode<IssueFaultType>;
    selectedIds: Set<string>;
    handleSelect: (ids: string[]) => void;
    handleDeselect: (ids: string[]) => void;
}>;

function FaultTypeChildrenSelector({
    node,
    selectedIds,
    handleSelect,
    handleDeselect,
}: FaultTypeChildrenSelectorProps) {
    const allChildrenSelectedCount = node.allChildren.reduce(
        (accum, cur) => (selectedIds.has(cur.id) ? accum + 1 : accum),
        0,
    );

    const isThisSelected = selectedIds.has(node.id);
    const selectedCount = +isThisSelected + allChildrenSelectedCount;

    const areAllChildrenSelected = allChildrenSelectedCount === node.allChildren.length;

    if (node.allChildren.length === 0) {
        return null;
    }

    return (
        <ChildrenLozenge
            className="btn"
            $hasAnySelected={!!selectedCount}
            onClick={(e) => {
                stopEvent(e);
                if (areAllChildrenSelected) {
                    handleDeselect([node.id, ...node.allChildren.map((el) => el.id)]);
                } else {
                    handleSelect([node.id, ...node.allChildren.map((el) => el.id)]);
                }
            }}
            title={
                areAllChildrenSelected
                    ? 'Deselect this and all children'
                    : 'Select this and all children'
            }
        >
            <FontAwesomeIcon
                className="icon"
                icon={areAllChildrenSelected ? faMinus : faPlus}
            />

            <span>
                {selectedCount} / {node.allChildren.length + 1}
            </span>
        </ChildrenLozenge>
    );
}

export type FaultTypeNodeProps = Readonly<{
    node: TreeNode<IssueFaultType>;
    filterText: string;
    showChildrenSelector: boolean;
    selectedIds: Set<string>;
    handleSelect: (ids: string[]) => void;
    handleDeselect: (ids: string[]) => void;
}>;

export function FaultTypeNode({
    node,
    filterText,
    showChildrenSelector,
    selectedIds,
    handleSelect,
    handleDeselect,
}: FaultTypeNodeProps) {
    const faultTypeName = node.isRoot ? node.data.name.toUpperCase() : node.data.name;

    const FaultTypeTreeItemContainer = showChildrenSelector
        ? FaultTypeTreeItemWithTrailer
        : FaultTypeTreeItem;

    return filterText ? (
        <FaultTypeTreeItemContainer $isRoot={node.isRoot}>
            <HighlightedText text={faultTypeName} terms={[filterText]} />
        </FaultTypeTreeItemContainer>
    ) : (
        <FaultTypeTreeItemContainer
            $isRoot={node.isRoot}
            className="flex flex-row justify-between"
        >
            <div className="flex flex-row align-center gap-5">
                <div>{faultTypeName}</div>

                {node.data.internalOnly && <FontAwesomeIcon icon={faShieldHalved} />}
            </div>

            {showChildrenSelector && (
                <FaultTypeChildrenSelector
                    node={node}
                    selectedIds={selectedIds}
                    handleSelect={handleSelect}
                    handleDeselect={handleDeselect}
                />
            )}
        </FaultTypeTreeItemContainer>
    );
}
